/* =========================================================
   INSPO WEBSITE REBUILD
   Source target: screen recording
   Interactions:
   - Header scrolled state only
   - Right-side menu drawer
   - Inquiry modal
   - Smooth anchor scrolling
   - Reveal animations
   - Chapter video hover
   ========================================================= */

(function () {
  const body = document.body;

  const selectors = {
    header: ".inspo-header",
    menuOpen: "[data-inspo-menu-open]",
    menuClose: "[data-inspo-menu-close]",
    menuBackdrop: ".inspo-menu-backdrop",
    menuLinks: ".inspo-menu-drawer a, .inspo-menu-drawer button",
    modalOpen: "[data-inspo-modal-open]",
    modalClose: "[data-inspo-modal-close]",
    modal: ".inspo-modal",
    reveal: ".inspo-reveal",
    chapterCard: ".inspo-chapter-card"
  };

  function updateBodyLock() {
    const menuOpen = body.classList.contains("inspo-menu-open");
    const modalOpen = body.classList.contains("inspo-modal-open");

    if (menuOpen || modalOpen) {
      body.classList.add("inspo-lock-scroll");
    } else {
      body.classList.remove("inspo-lock-scroll");
    }
  }

  /* -------------------------------
     Header scrolled background only
     Header hide/show is controlled globally
     from layout/theme.liquid
  -------------------------------- */

  function initHeader() {
    const header = document.querySelector(selectors.header);

    if (!header) return;

    function getScrollTop() {
      return (
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        document.scrollingElement?.scrollTop ||
        0
      );
    }

    function updateScrolledState() {
      const currentScrollY = getScrollTop();

      if (currentScrollY > 10) {
        header.classList.add("is-scrolled");
      } else {
        header.classList.remove("is-scrolled");
      }
    }

    updateScrolledState();

    window.addEventListener("scroll", updateScrolledState, { passive: true });
    document.addEventListener("scroll", updateScrolledState, { passive: true });
  }

  /* -------------------------------
     Menu drawer
  -------------------------------- */

  function openMenu() {
    body.classList.add("inspo-menu-open");
    updateBodyLock();
    document.dispatchEvent(new CustomEvent("inspo:menu-opened"));
  }

  function closeMenu() {
    body.classList.remove("inspo-menu-open");
    updateBodyLock();
    document.dispatchEvent(new CustomEvent("inspo:menu-closed"));
  }

  function initMenu() {
    const openButtons = document.querySelectorAll(selectors.menuOpen);
    const closeButtons = document.querySelectorAll(selectors.menuClose);
    const backdrop = document.querySelector(selectors.menuBackdrop);
    const menuLinks = document.querySelectorAll(selectors.menuLinks);

    openButtons.forEach((button) => {
      button.addEventListener("click", function () {
        if (body.classList.contains("inspo-menu-open")) {
          closeMenu();
        } else {
          openMenu();
        }
      });
    });

    closeButtons.forEach((button) => {
      button.addEventListener("click", closeMenu);
    });

    if (backdrop) {
      backdrop.addEventListener("click", closeMenu);
    }

    menuLinks.forEach((link) => {
      link.addEventListener("click", function () {
        const isModalTrigger = link.hasAttribute("data-inspo-modal-open");

        if (!isModalTrigger) {
          closeMenu();
        }
      });
    });
  }

  /* -------------------------------
     Inquiry modal
  -------------------------------- */

  function openModal() {
    closeMenu();
    body.classList.add("inspo-modal-open");
    updateBodyLock();
    document.dispatchEvent(new CustomEvent("inspo:modal-opened"));
  }

  function closeModal() {
    body.classList.remove("inspo-modal-open");
    updateBodyLock();
    document.dispatchEvent(new CustomEvent("inspo:modal-closed"));
  }

  function initModal() {
    const openButtons = document.querySelectorAll(selectors.modalOpen);
    const closeButtons = document.querySelectorAll(selectors.modalClose);
    const modal = document.querySelector(selectors.modal);

    openButtons.forEach((button) => {
      button.addEventListener("click", function (event) {
        event.preventDefault();
        openModal();
      });
    });

    closeButtons.forEach((button) => {
      button.addEventListener("click", closeModal);
    });

    if (modal) {
      modal.addEventListener("click", function (event) {
        if (event.target === modal) {
          closeModal();
        }
      });
    }
  }

  /* -------------------------------
     Smooth scrolling
  -------------------------------- */

  function initSmoothScroll() {
    const anchorLinks = document.querySelectorAll('a[href^="#"]');

    anchorLinks.forEach((link) => {
      link.addEventListener("click", function (event) {
        const href = link.getAttribute("href");

        if (!href || href === "#") return;

        const target = document.querySelector(href);

        if (!target) return;

        event.preventDefault();

        closeMenu();
        closeModal();

        const targetTop = target.getBoundingClientRect().top + window.pageYOffset;

        window.scrollTo({
          top: targetTop,
          behavior: "smooth"
        });
      });
    });
  }

  /* -------------------------------
     Reveal animations
  -------------------------------- */

  function initReveal() {
    const revealItems = document.querySelectorAll(selectors.reveal);

    if (!revealItems.length) return;

    if (!("IntersectionObserver" in window)) {
      revealItems.forEach((item) => {
        item.classList.add("is-visible");
      });
      return;
    }

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.16,
        rootMargin: "0px 0px -8% 0px"
      }
    );

    revealItems.forEach((item) => {
      observer.observe(item);
    });
  }

  /* -------------------------------
     Chapter video hover
  -------------------------------- */

  function initChapterVideoHover() {
    const chapterCards = document.querySelectorAll(selectors.chapterCard);

    chapterCards.forEach((card) => {
      const video = card.querySelector("video");

      if (!video) return;

      card.addEventListener("mouseenter", function () {
        video.play().catch(function () {});
      });

      card.addEventListener("mouseleave", function () {
        video.pause();
        video.currentTime = 0;
      });
    });
  }

  /* -------------------------------
     Keyboard controls
  -------------------------------- */

  function initKeyboardControls() {
    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape") {
        closeMenu();
        closeModal();
      }
    });
  }

  /* -------------------------------
     Init
  -------------------------------- */

  document.addEventListener("DOMContentLoaded", function () {
    initHeader();
    initMenu();
    initModal();
    initSmoothScroll();
    initReveal();
    initChapterVideoHover();
    initKeyboardControls();
  });
})();